import d3 from 'd3';
import _ from 'underscore';
import moment from 'moment';
import SpinJS from 'spin.js';
import * as AnalyticAppColor from 'common/constants/analytic-app-color';
import {
  AnalyticAppInteractionEnum,
  AnalyticAppRendererEnum,
  ReportingPeriodEnum,
} from 'common/proxies/enum-proxy';
import Interpolation from 'charts/constants/interpolation';
import {getEndOfPeriod} from 'common/utils/date-util';

const lucid = {
  /**
   * Return the x and y coordinates of
   * the center of the content area.
   *
   * @param contentWidth
   * @param contentHeight
   * @returns {{x: number, y: number}}
   */
  getCenter(contentWidth, contentHeight) {
    return {
      x: contentWidth / 2,
      y: contentHeight / 2,
    };
  },

  /**
   * Returns the height of the area into which
   * chart content can be rendered.
   *
   * @param height Total height of parent SVG.
   * @param margins Chart margins.
   * @returns {number}
   */
  getContentHeight(height, margins) {
    return height - margins.bottom - margins.top;
  },

  /**
   * Returns the width of the area into which
   * chart content can be rendered.
   *
   * @param width Total width of parent SVG.
   * @param margins Chart margins.
   * @returns {number}
   */
  getContentWidth(width, margins) {
    return width - margins.left - margins.right;
  },

  /**
   * Returns the min and max values in a data series,
   * for use with a linear scale.
   *
   * d3 calls these values a "domain".
   *
   * @param values
   * @param clampToZero
   * @returns {*}
   */
  getLinearDomain(values, clampToZero) {
    const domainY = d3.extent(values, (datum) => {
      return datum.value;
    });
    if (domainY[0] > 0 && clampToZero) {
      domainY[0] = 0;
    }
    return domainY;
  },

  /**
   * Returns a d3 linear scale, for use with
   * number-based chart axes.
   *
   * In d3, a scale converts data values into
   * pixel coordinates and vice versa.
   *
   * @param domain
   * @param range
   * @returns {*}
   */
  getLinearScale(domain, range) {
    return d3.scale.linear().domain(domain).range(range);
  },

  /**
   * Returns the value of contentWidth minus
   * horizontal padding. This is important for
   * column charts, which must often be
   * adjusted inwards.
   *
   * @param contentWidth
   * @param padding
   * @returns {*[]}
   */
  getRangeX(contentWidth, padding) {
    return [0, contentWidth - padding.left - padding.right];
  },

  /**
   * Returns the value of contentHeight minus
   * vertical padding. This is usually used to
   * adjust charts inward a little from the top,
   * in order to make room for a legend.
   *
   * @param contentHeight
   * @param padding
   * @returns {*[]}
   */
  getRangeY(contentHeight, padding) {
    return [contentHeight - padding.bottom - padding.top, 0];
  },

  /**
   * Returns the earliest and latest dates in a
   * data series, for use with a time scale.
   *
   * d3 calls these values a "domain".
   *
   * @param start
   * @param end
   * @param period
   * @returns {Array}
   */
  getTimeDomain(start, end, period) {
    const domain = [];
    switch (period.slug) {
      case ReportingPeriodEnum.LESS_THAN_TWELVE_MONTHS.slug:
      case ReportingPeriodEnum.MORE_THAN_TWELVE_MONTHS.slug:
        domain[0] = moment(start).startOf('month').toDate();
        break;
      default:
        domain[0] = start;
        break;
    }
    domain[1] = getEndOfPeriod(start, period, false);
    if (!domain[1]) {
      domain[1] = end;
    }
    return domain;
  },

  /**
   * Returns a d3 time scale, for use with
   * date-based chart axes.
   *
   * In d3, a scale converts data values into
   * pixel coordinates and vice versa.
   *
   * @param domain
   * @param range
   * @returns {*}
   */
  getTimeScale(domain, range) {
    return d3.time.scale().domain(domain).range(range);
  },
};

/**
 * Constants for common CSS class names.
 */
lucid.classes = {
  AREA_GRID_LINES: 'area-grid-lines',
  BACKGROUND_GRID_LINES: 'background-grid-lines',
  BACKGROUND_RECT: 'background-rect',
  BORDER_RECT: 'border-rect',
  CONTENT_BACKGROUND_RECT: 'content-background-rect',
  FADE_RECT: 'fade-rect',
  MESSAGE: 'message',
  SPINNER: 'spinner',
  SVG: 'svg',
  SVG_CONTAINER: 'svgContainer',
  SVG_DIV: 'svgParentDiv',
  X_AXIS: 'x axis',
  Y_AXIS: 'y axis',
  Y_AXIS_LABEL: 'y-axis-label',
};

/**
 * Constants for common element IDs.
 */
lucid.id = {
  ARROW_BUTTONS: 'arrowButtons',
  ARROW_BUTTONS_LABEL: 'arrowButtonsLabel',
  BACKGROUND_GRID_LINES: 'backgroundGridLines',
  CONTENT: 'contentContainer',
  CONTENT_CLIP_PATH: 'contentClipPath',
  DEFS: 'defs',
  FADE_RECT: 'fadeRect',
  LEGEND: 'legend',
  GLOW: 'glow',
  MESSAGE: 'message',
  SERIES: 'seriesContainer',
  SHADOW: 'shadow',
  SVG_CONTAINER: 'svgContainer',
  SVG_DIV: 'svgParentDiv',
  SVG: 'svg',
};

/**
 * Constants for chart view state names.
 * Charts must implement these states.
 */
lucid.states = {
  EMPTY: 'empty',
  INIT: 'init',
  LOADING: 'loading',
  MESSAGE: 'message',
  NORMAL: 'normal',
};

/**
 * SVG manipulation functions
 */
lucid.svg = {
  /**
   * Appends the requested SVG element and
   * sets its attributes and styles.
   *
   * @param element
   * @param selection
   * @param props
   * @returns {*|void}
   */
  append(element, selection, props) {
    const appended = selection.append(element);
    lucid.svg.setProps(appended, props);
    return appended;
  },

  /**
   * Filter definitions.
   *
   * @param selection
   * @returns {void|*}
   */
  defs(selection) {
    const defs = selection.append('defs').attr('id', lucid.id.DEFS);

    const shadow = defs
      .append('filter')
      .attr('height', '130%')
      .attr('id', lucid.id.SHADOW);
    shadow
      .append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', 2);
    shadow.append('feOffset').attr('dy', 2).attr('result', 'offsetblur');
    shadow
      .append('feComponentTransfer')
      .append('feFuncA')
      .attr('slope', 0.2)
      .attr('type', 'linear');
    const shadowMerge = shadow.append('feMerge');
    shadowMerge.append('feMergeNode');
    shadowMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    const glow = defs.append('filter').attr('id', lucid.id.GLOW);
    glow
      .append('feColorMatrix')
      .attr('type', 'matrix')
      .attr('values', '1 0 0 0 0 1 0 0 0 0 1 0 0 0 0 0 0 0 0.7 0');
    glow
      .append('feGaussianBlur')
      .attr('stdDeviation', 2.5)
      .attr('result', 'coloredBlur');
    const glowMerge = glow.append('feMerge');
    glowMerge.append('feMergeNode').attr('in', 'coloredBlur');
    glowMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    return defs;
  },

  /**
   * Appends a parent div for SVG.
   *
   * @param selection
   * @param props
   * @returns {*}
   */
  div(selection, props) {
    const div = selection.append('div');
    lucid.svg.setProps(div, props);
    return div;
  },

  /**
   * Returns an SVG path as a series of points.
   *
   * @param path
   * @returns {Array}
   */
  getPointsFromPath(path) {
    const node = path.node();
    const length = Math.floor(node.getTotalLength());
    const points = [];
    for (let ii = 0, nn = length; ii < nn; ii++) {
      points.push(node.getPointAtLength(ii));
    }
    return points;
  },

  /**
   * Appends a group and sets its attributes and styles.
   *
   * @param selection
   * @param props
   * @returns {*}
   */
  group(selection, props) {
    const group = selection.append('g');
    lucid.svg.setProps(group, props);
    return group;
  },

  /**
   * Appends a rect and sets its attributes and styles.
   *
   * @param selection
   * @param props
   * @returns {*}
   */
  rect(selection, props) {
    const rect = selection.append('rect');
    lucid.svg.setProps(rect, props);
    return rect;
  },

  /**
   * Removes all elements of the passed
   * CSS class.
   *
   * @param className
   * @param selection optional
   */
  removeByClassName(className, selection) {
    return selection
      ? selection.selectAll(`.${className}`).remove()
      : d3.selectAll(`.${className}`).remove();
  },

  /**
   * Removes any element with the passed ID.
   *
   * @param identifier
   */
  removeByID(id) {
    return d3.select(`#${id}`).remove();
  },

  /**
   * Removes the loading spinner and
   * associated listeners.
   *
   * @param selection
   */
  removeSpinner(selection) {
    $(window).unbind('resize');
    selection.selectAll('.spinner').remove();
  },

  /**
   * Appends a root SVG element for a chart.
   *
   * @param selection
   * @param width
   * @param height
   * @returns {*}
   */
  root(selection, props) {
    const svg = selection.append('svg');
    // SVG, like Flash, needs an extra pixel
    // If it's going to display the full
    // Dimensions that we request.
    if (props && props.length) {
      // The first item in props is an object
      // Of attributes for the new element.
      if (props[0].hasOwnProperty('width')) {
        props[0].width += 1;
      }
      if (props[0].hasOwnProperty('height')) {
        props[0].height += 1;
      }
    }
    lucid.svg.setProps(svg, props);
    return svg;
  },

  /**
   * D3 selects all elements of the specified CSS class.
   *
   * @param className
   * @returns {*}
   */
  selectByClassName(className) {
    return d3.selectAll(`.${className}`);
  },

  /**
   * D3 selects the element with the specified ID.
   *
   * @param id
   * @returns {*}
   */
  selectByID(id) {
    return d3.select(`#${id}`);
  },

  /**
   * Sets attributes on a d3 selection.
   *
   * @param target
   * @param attributes
   */
  setAttributes(selection, attributes) {
    if (attributes) {
      for (const propName in attributes) {
        selection.attr(propName, attributes[propName]);
      }
    }
  },

  /**
   * Convenience function for setting atts
   * and styles on d3 selections. Gets you
   * past d3's repetitive chaining syntax.
   *
   * @param selection
   * @param props
   */
  setProps(selection, props) {
    if (props && props.length) {
      lucid.svg.setAttributes(selection, props[0]);
      lucid.svg.setStyles(selection, props[1]);
    }
  },

  /**
   * Sets styles on a d3 selection.
   *
   * @param selection
   * @param styles
   */
  setStyles(selection, styles) {
    if (styles) {
      for (const propName in styles) {
        selection.style(propName, styles[propName]);
      }
    }
  },

  /**
   * Appends a loading spinner.
   *
   * @param selection
   * @returns {*}
   */
  spinner(selection, props) {
    lucid.svg.removeSpinner(selection);
    $(window).bind('resize', () => {
      // Re-center on resize:
      lucid.svg.removeSpinner(selection);
      lucid.svg.spinner(selection);
    });
    return new SpinJS(
      _.extend(
        {
          className: lucid.classes.SPINNER,
          color: AnalyticAppColor.BLACK.primary,
          corners: 1,
          hwaccel: true,
          top: '50%',
          left: '50%',
          length: 9,
          lines: 11,
          radius: 13,
          rotate: 15,
          shadow: false,
          speed: 1,
          trail: 40,
          width: 4,
          zIndex: 2e9,
        },
        props || {},
      ),
    ).spin(selection.node());
  },

  /**
   * Appends a text object and sets its attributes and styles.
   *
   * @param selection
   * @param props
   * @returns {*}
   */
  text(selection, props) {
    const text = selection.append('text');
    lucid.svg.setProps(text, props);
    return text;
  },
};

/**
 * Reference objects for chart types.
 */
lucid.series = {
  getHeatmap() {
    return {
      comparison: undefined,
      comparisonData: undefined,
      endTimestamp: undefined,
      facility: undefined,
      meter: undefined,
      notes: [],
      range: undefined,
      results: [],
      startTimestamp: undefined,
    };
  },
  getHorizontalBarChart() {
    return {
      color: undefined,
      comparisonEnd: undefined,
      comparisonStart: undefined,
      comparisonType: 'none',
      end: undefined,
      min: NaN,
      max: NaN,
      results: [],
      start: undefined,
      unit: undefined,
    };
  },
  getTimeseries() {
    return {
      animate: true,
      axisLabelY: 'axis label y',
      capped: false,
      clampToZero: true,
      compact: false,
      displayName: 'meter name',
      end: moment(),
      fill: undefined,
      gridLineStroke: '#FF00CC',
      gridLineStrokeOpacity: 0.5,
      hideMarker: false,
      includeInLegend: true,
      insert: undefined,
      interaction: AnalyticAppInteractionEnum.NONE,
      interpolate: Interpolation.CARDINAL,
      renderer: AnalyticAppRendererEnum.LINE,
      start: moment(),
      stretch: false,
      stroke: undefined,
      strokeStyle: 'solid',
      strokeWidth: 4,
      unit: undefined,
      values: [],
    };
  },
};

export default lucid;
